/**
 * Group per-contact ProcessedInteraction rows into session-level
 * conversation units.
 *
 * The `interactions` table stores one row per (contact, session) pair
 * so that contact-detail views can cleanly query "every conversation
 * I've had with Henry." That's the right storage shape.
 *
 * But Recent Interactions on the Me tab should show ONE row per actual
 * conversation — not N rows per conversation-with-N-contacts. Pure UI
 * grouping, no schema change: collect interactions sharing a sessionId,
 * merge participant names + topics + people mentions, pick the richest
 * available summary, and render as a unified session card.
 */
import type { Contact } from "@/lib/data";
import type {
  ConversationFacts,
  ProcessedInteraction,
} from "@/types/voice";
import { displayNameForContactId } from "@/utils/interactionDisplay/interactionDisplay";

export interface SessionInteractionGroup {
  sessionId: string;
  /** Per-contact interaction rows in this session, stable order. */
  participants: ProcessedInteraction[];
  /** Display names of the participants, stable order. */
  participantNames: string[];
  /** Apple Contacts ids (or "user_self") for every participant. */
  participantContactIds: string[];
  /** Representative timestamp — latest createdAt across the group. */
  createdAt: string;
  /** Representative summary — picks the richest non-empty one. */
  summary: string;
  /** Earliest first-turn across the group (session start). */
  firstTurnAtSeconds: number;
  /** Latest last-turn across the group (session end). */
  lastTurnAtSeconds: number;
  /** Sum of total-seconds-spoken across all participants. */
  totalSecondsSpoken: number;
  /** Merged topics across all participants' facts. De-duplicated. */
  topics: string[];
  /** Merged "people mentioned" across all facts. De-duplicated. */
  peopleMentioned: string[];
  /** Union of highlights from each participant's facts (capped). */
  highlights: string[];
  /** Union of notable quotes across the group (capped). */
  notableQuotes: string[];
  /** Union of commitments across the group. */
  commitments: string[];
  /** Union of open questions across the group. */
  openQuestions: string[];
  /** Strongest sentiment tag across the group (first non-empty). */
  sentiment: string;
  /** Location if the session had one — first non-null wins. */
  locationLabel: string | null;
  latitude: number | null;
  longitude: number | null;
}

/**
 * Group a flat list of ProcessedInteraction rows by sessionId and
 * produce one SessionInteractionGroup per session. Output ordered by
 * most-recent createdAt descending, matching the existing Recent
 * Interactions feed order.
 */
export function groupInteractionsBySession(
  interactions: ProcessedInteraction[],
  contacts: Contact[]
): SessionInteractionGroup[] {
  const bySession = new Map<string, ProcessedInteraction[]>();
  for (const row of interactions) {
    const list = bySession.get(row.sessionId);
    if (list) list.push(row);
    else bySession.set(row.sessionId, [row]);
  }

  const groups: SessionInteractionGroup[] = [];
  for (const [sessionId, rows] of bySession.entries()) {
    const sorted = [...rows].sort((a, b) =>
      a.contactId.localeCompare(b.contactId)
    );

    const participantNames = sorted.map((r) =>
      displayNameForContactId(r.contactId, contacts)
    );
    const participantContactIds = sorted.map((r) => r.contactId);

    const createdAt = sorted
      .map((r) => r.createdAt)
      .sort()
      .pop() ?? sorted[0].createdAt;

    const firstTurn = Math.min(...sorted.map((r) => r.firstTurnAtSeconds));
    const lastTurn = Math.max(...sorted.map((r) => r.lastTurnAtSeconds));
    const totalSpoken = sorted.reduce((acc, r) => acc + r.totalSecondsSpoken, 0);
    const summary = pickRichestSummary(sorted, participantNames, totalSpoken);

    const mergedFacts = mergeFacts(sorted.map((r) => r.facts));

    const locRow =
      sorted.find(
        (r) => r.locationLabel !== null || (r.latitude !== null && r.longitude !== null)
      ) ?? null;

    groups.push({
      sessionId,
      participants: sorted,
      participantNames,
      participantContactIds,
      createdAt,
      summary,
      firstTurnAtSeconds: firstTurn,
      lastTurnAtSeconds: lastTurn,
      totalSecondsSpoken: totalSpoken,
      topics: mergedFacts.topics,
      peopleMentioned: mergedFacts.peopleMentioned,
      highlights: mergedFacts.highlights,
      notableQuotes: mergedFacts.notableQuotes,
      commitments: mergedFacts.commitments,
      openQuestions: mergedFacts.openQuestions,
      sentiment: mergedFacts.sentiment,
      locationLabel: locRow?.locationLabel ?? null,
      latitude: locRow?.latitude ?? null,
      longitude: locRow?.longitude ?? null,
    });
  }

  groups.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  return groups;
}

/**
 * Pick the best real summary across a session's interactions, or fall
 * back to a mechanical description if none exists yet. Intentionally
 * NEVER returns a raw transcript line — the on-device model refuses
 * some conversations (strict guardrails), and showing a single random
 * speaker turn as the "summary" misrepresents the conversation. A
 * truthful "N-turn conversation with …" line is better than a
 * misleading quote.
 */
function pickRichestSummary(
  rows: ProcessedInteraction[],
  participantNames: string[],
  totalSecondsSpoken: number
): string {
  const candidates: string[] = [];
  for (const row of rows) {
    const s = row.facts?.summary?.trim() || row.summary?.trim();
    if (s) candidates.push(s);
  }
  if (candidates.length > 0) {
    return candidates.reduce((best, cur) =>
      cur.length > best.length ? cur : best
    );
  }
  return buildMechanicalSummary(participantNames, totalSecondsSpoken);
}

function buildMechanicalSummary(
  names: string[],
  totalSecondsSpoken: number
): string {
  const minutes = Math.max(1, Math.round(totalSecondsSpoken / 60));
  const duration = `${minutes}-minute conversation`;
  const who = formatNamesSentence(names);
  if (!who) return `${duration}. Summary not available yet.`;
  return `${duration} with ${who}. Summary not available yet.`;
}

function formatNamesSentence(names: string[]): string {
  const clean = names.filter((n) => n && n.trim());
  if (clean.length === 0) return "";
  if (clean.length === 1) return clean[0];
  if (clean.length === 2) return `${clean[0]} and ${clean[1]}`;
  const head = clean.slice(0, -1).join(", ");
  return `${head}, and ${clean[clean.length - 1]}`;
}

interface MergedFacts {
  topics: string[];
  peopleMentioned: string[];
  highlights: string[];
  notableQuotes: string[];
  commitments: string[];
  openQuestions: string[];
  sentiment: string;
}

function mergeFacts(factsList: (ConversationFacts | null)[]): MergedFacts {
  const topics = new Set<string>();
  const people = new Set<string>();
  const highlights: string[] = [];
  const quotes: string[] = [];
  const commitments: string[] = [];
  const openQuestions: string[] = [];
  let sentiment = "";

  for (const f of factsList) {
    if (!f) continue;
    for (const t of f.topics) topics.add(t);
    for (const p of f.peopleMentioned) people.add(p);
    highlights.push(...f.highlights);
    quotes.push(...f.notableQuotes);
    commitments.push(...f.commitments);
    openQuestions.push(...f.openQuestions);
    if (!sentiment && f.sentiment?.trim()) sentiment = f.sentiment.trim();
  }

  return {
    topics: Array.from(topics),
    peopleMentioned: Array.from(people),
    highlights: dedupeAndCap(highlights, 8),
    notableQuotes: dedupeAndCap(quotes, 6),
    commitments: dedupeAndCap(commitments, 8),
    openQuestions: dedupeAndCap(openQuestions, 8),
    sentiment,
  };
}

function dedupeAndCap(items: string[], limit: number): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of items) {
    const normalized = item.trim();
    if (!normalized) continue;
    const key = normalized.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(normalized);
    if (out.length >= limit) break;
  }
  return out;
}

/**
 * Human-readable participant list for a session card header.
 *   ["Henry"]                      → "Henry"
 *   ["Henry", "Bill"]              → "Henry and Bill"
 *   ["Henry", "Bill", "Bobby"]     → "Henry, Bill, and Bobby"
 *   ["Henry", "Bill", "Bobby", ...] (>3) → "Henry, Bill, Bobby + N more"
 */
export function formatParticipants(names: string[]): string {
  if (names.length === 0) return "";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  if (names.length === 3)
    return `${names[0]}, ${names[1]}, and ${names[2]}`;
  const head = names.slice(0, 3).join(", ");
  return `${head} + ${names.length - 3} more`;
}
